import { useEffect, useMemo } from 'react';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { cleanEmpty } from 'utils/cleanEmpty';

import { MerchantShowAccessListFilterChips } from './MerchantShowAccessListFilterChips';
import { MerchantShowAccessListFilterForm } from './MerchantShowAccessListFilterForm';

export type MerchantShowAccessFilterValues = {
  user: {
    firstName: string;
    lastName: string;
    roleEntities: string[];
  };
};

const DEFAULT_FILTER_VALUES = {
  user: {
    firstName: '',
    lastName: '',
    roleEntities: [],
  },
};

export const MerchantShowAccessListFilter: React.FC = () => {
  const translate = useTypedTranslate();

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<MerchantShowAccessFilterValues>({ defaultValues: DEFAULT_FILTER_VALUES });

  const { setFilters, displayedFilters } = useListContext();

  useEffect(() => {
    setFilters(appliedFilters, displayedFilters);
  }, [appliedFilters]);

  const isListFilterExist = useMemo(
    () => Object.keys(cleanEmpty(appliedFilters)).length > 0,
    [appliedFilters],
  );

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterClearButton
            key="clear"
            onClick={() => {
              onReset();
            }}
            visible={isListFilterExist}
          />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <MerchantShowAccessListFilterForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label={translate('app.buttons.reset')} onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <MerchantShowAccessListFilterChips />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
